import { siteInfo, faqs } from '@/mocks/site';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function AboutPage() {
  const aboutSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: '6080影院',
        url: 'https://www.6080cinema.com',
        description: siteInfo.description,
        foundingDate: siteInfo.founded,
        contactPoint: {
          '@type': 'ContactPoint',
          email: siteInfo.contact.email,
          telephone: siteInfo.contact.phone,
          contactType: 'customer service',
          availableLanguage: ['Chinese'],
        },
        address: {
          '@type': 'PostalAddress',
          streetAddress: siteInfo.contact.address,
          addressCountry: 'CN',
        },
      },
      {
        '@type': 'FAQPage',
        mainEntity: faqs.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer,
          },
        })),
      },
      {
        '@type': 'WebPage',
        name: '关于6080影院 - 电影大全平台',
        description: '了解6080影院品牌故事、口碑评价及联系方式。国内领先的电影大全平台，提供最新电影、经典电影、动作电影、喜剧电影免费高清在线观看。',
        url: 'https://www.6080cinema.com/about',
      },
    ],
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.6080cinema.com' },
      { '@type': 'ListItem', position: 2, name: '关于我们' },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <div className="w-full px-4 md:px-6 py-4">
        <div className="max-w-[1280px] mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          <section className="mt-8 mb-14">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4 font-heading">关于6080影院</h1>
            <div className="max-w-3xl">
              <p className="text-sm text-foreground-600 leading-relaxed">{siteInfo.brandStory}</p>
            </div>
          </section>

          <section className="mb-14">
            <h2 className="text-lg md:text-xl font-bold text-foreground-950 mb-6 font-heading">平台数据</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {siteInfo.stats.map((stat) => (
                <div key={stat.label} className="p-6 bg-background-100 rounded-lg text-center border border-background-200/60">
                  <p className="text-2xl md:text-3xl font-bold text-accent-600 font-heading">{stat.value}</p>
                  <p className="text-sm text-foreground-500 mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-14">
            <h2 className="text-lg md:text-xl font-bold text-foreground-950 mb-6 font-heading">核心优势</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {siteInfo.features.map((feature) => (
                <div key={feature.title} className="p-6 bg-background-100 rounded-lg border border-background-200/60">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-500/10 mb-4">
                    <i className="ri-check-double-line text-xl text-accent-600"></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2 font-heading">{feature.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-14">
            <h2 className="text-lg md:text-xl font-bold text-foreground-950 mb-6 font-heading">用户口碑</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {siteInfo.testimonials.map((t) => (
                <div key={t.name} className="p-6 bg-background-100 rounded-lg border border-background-200/60">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">&ldquo;{t.content}&rdquo;</p>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-accent-500/10 flex items-center justify-center">
                      <span className="text-xs font-bold text-accent-600">{t.name.charAt(0)}</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{t.name}</p>
                      <p className="text-xs text-foreground-500">{t.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-14">
            <h2 className="text-lg md:text-xl font-bold text-foreground-950 mb-6 font-heading">常见问题</h2>
            <div className="max-w-3xl space-y-3">
              {faqs.map((faq, idx) => (
                <details key={idx} className="group bg-background-100 rounded-lg border border-background-200/60">
                  <summary className="flex items-center justify-between p-4 md:p-5 cursor-pointer">
                    <h3 className="text-sm font-medium text-foreground-900 pr-4">{faq.question}</h3>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform flex-shrink-0"></i>
                  </summary>
                  <div className="px-4 md:px-5 pb-4 md:pb-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </section>

          <section className="mb-14">
            <h2 className="text-lg md:text-xl font-bold text-foreground-950 mb-6 font-heading">联系我们</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              <div className="p-6 bg-background-100 rounded-lg border border-background-200/60 flex items-start gap-4">
                <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-accent-500/10">
                  <i className="ri-mail-line text-accent-600"></i>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">邮箱</h3>
                  <p className="text-sm text-foreground-500">{siteInfo.contact.email}</p>
                </div>
              </div>
              <div className="p-6 bg-background-100 rounded-lg border border-background-200/60 flex items-start gap-4">
                <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-accent-500/10">
                  <i className="ri-wechat-line text-accent-600"></i>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">微信</h3>
                  <p className="text-sm text-foreground-500">{siteInfo.contact.wechat}</p>
                </div>
              </div>
              <div className="p-6 bg-background-100 rounded-lg border border-background-200/60 flex items-start gap-4">
                <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-accent-500/10">
                  <i className="ri-phone-line text-accent-600"></i>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">电话</h3>
                  <p className="text-sm text-foreground-500">{siteInfo.contact.phone}</p>
                </div>
              </div>
              <div className="p-6 bg-background-100 rounded-lg border border-background-200/60 flex items-start gap-4">
                <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-accent-500/10">
                  <i className="ri-time-line text-accent-600"></i>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">工作时间</h3>
                  <p className="text-sm text-foreground-500">{siteInfo.contact.workingHours}</p>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}